export const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT_TO_SHARP = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };

export function transposeChord(chord, semitones) {
  if (!chord || semitones === 0) return chord;
  const m = chord.match(/^([A-G][b#]?)(.*)$/);
  if (!m) return chord;
  let root = m[1];
  const suffix = m[2] || "";
  if (FLAT_TO_SHARP[root]) root = FLAT_TO_SHARP[root];
  const idx = NOTES.indexOf(root);
  if (idx === -1) return chord;
  const next = NOTES[(idx + semitones + 1200) % 12];
  return next + suffix;
}

export function transposeKey(key, semitones) {
  if (!key) return key;
  const parts = key.split(" ");
  return transposeChord(parts[0], semitones) + (parts[1] ? " " + parts.slice(1).join(" ") : "");
}

export function fmtTime(s) {
  if (s == null || isNaN(s)) return "0:00";
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export const SATB = [
  { key: "vocals", label: "All Vocals", color: "#F59E0B", ai: false },
  { key: "soprano", label: "Soprano", color: "#EC4899", ai: true },
  { key: "alto", label: "Alto", color: "#A855F7", ai: true },
  { key: "tenor", label: "Tenor", color: "#3B82F6", ai: true },
  { key: "bass", label: "Bass (voice)", color: "#10B981", ai: true },
  { key: "instrumental", label: "Instrumental", color: "#64748B", ai: false },
];

export const INSTRUMENTS = [
  { key: "instrumental", label: "Full Mix", color: "#06B6D4", ai: false },
  { key: "drums", label: "Drums", color: "#EF4444", ai: true },
  { key: "bass_guitar", label: "Bass", color: "#8B5CF6", ai: true },
  { key: "piano", label: "Piano", color: "#10B981", ai: true },
  { key: "guitar", label: "Guitar", color: "#06B6D4", ai: true },
  { key: "strings", label: "Strings", color: "#F43F5E", ai: true },
  { key: "synth", label: "Synth/Keys", color: "#D946EF", ai: true },
  { key: "other", label: "Other", color: "#64748B", ai: true },
];

export const SECTION_COLORS = {
  Intro: "#64748B",
  Verse: "#06B6D4",
  Chorus: "#F59E0B",
  Bridge: "#8B5CF6",
  Instrumental: "#10B981",
  Outro: "#EF4444",
  Other: "#94A3B8",
};

// DEMO 3-band EQ mapping for each stem so mixer faders/mute/solo are audible.
// (Approximation — real per-stem audio requires a connected separation API.)
export const BAND_MAP = {
  vocals: { mid: 1, high: 1 },
  soprano: { high: 1 },
  alto: { mid: 0.7, high: 0.9 },
  tenor: { mid: 1, high: 0.4 },
  bass: { low: 0.5, mid: 0.9 },
  instrumental: { low: 1, mid: 0.6, high: 0.4 },
  drums: { low: 0.6, high: 1 },
  bass_guitar: { low: 1 },
  piano: { mid: 1, high: 0.5 },
  guitar: { mid: 1, high: 0.7 },
  strings: { mid: 0.9, high: 0.6 },
  synth: { mid: 0.7, high: 0.9 },
  other: { mid: 0.6, high: 0.3 },
};

export function computeBandGains(tracks, mixState) {
  const soloActive = tracks.some((t) => {
    const s = mixState[t.key];
    return s?.solo && !s?.muted;
  });
  const acc = { low: [], mid: [], high: [] };
  tracks.forEach((t) => {
    const s = mixState[t.key] || {};
    const vol = s.volume ?? 1;
    const audible = soloActive ? (s.solo && !s.muted) : !s.muted;
    const eff = audible ? vol : 0;
    const map = BAND_MAP[t.key] || { mid: 1 };
    ["low", "mid", "high"].forEach((b) => {
      if (map[b]) acc[b].push({ w: map[b], v: eff });
    });
  });
  const out = {};
  ["low", "mid", "high"].forEach((b) => {
    const arr = acc[b];
    if (!arr.length) { out[b] = 1; return; }
    const num = arr.reduce((a, x) => a + x.w * x.v, 0);
    const den = arr.reduce((a, x) => a + x.w, 0);
    out[b] = den ? num / den : 1;
  });
  return out;
}
